import { useEffect, useState } from 'react';
import { api, type AcctCorp, type BankAccountRow, type BankInPlanRow } from '../lib/api';
import { ErrorBox, money, MoneyInput } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';

/**
 * 게시판 #351 — 수기 입금(추정). 입금 내역 화면 「수기 입금(추정)」 창.
 * 법인·날짜·금액·내용을 넣으면 유동성 시재 입금에 더해진다 — 단 **그 법인 은행 거래내역이 아직 없는 날**만
 * (실제 입금 파일이 올라오면 그 날 추정은 저절로 빠진다 · 서버 `functions/src/liquidity.ts`).
 */
const today = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);

/** 계좌 표시 — 입금 내역 계좌 고르기와 같은 모양(별칭 · 은행 끝 세 자리). #472 */
export const planAccountLabel = (r: { bank: string | null; account_no: string | null; account_alias?: string | null; alias?: string | null }) => {
  if (!r.account_no) return '';
  const tail = r.account_no.replace(/[^0-9]/g, '').slice(-3);
  const alias = r.account_alias ?? r.alias ?? '';
  return `${alias ? `${alias} ` : ''}[${r.bank ?? ''} ${tail}]`;
};

export function BankInPlanForm({ corps, from, to, defaultCorp, defaultAccount, onClose }: {
  corps: AcctCorp[]; from: string; to: string; defaultCorp: number | null; defaultAccount?: number | null; onClose: () => void;
}) {
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [rows, setRows] = useState<BankInPlanRow[]>([]);
  const [nonce, setNonce] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 입력 줄 — editId 가 있으면 그 줄 고치기.
  const [editId, setEditId] = useState<number | null>(null);
  const [corpId, setCorpId] = useState<number | null>(defaultCorp ?? corps[0]?.corp_id ?? null);
  // #472 입금될 계좌 — 고른 법인의 쓰는 원화 계좌만.
  const [accs, setAccs] = useState<BankAccountRow[]>([]);
  const [accountId, setAccountId] = useState<number | null>(defaultAccount ?? null);
  useEffect(() => { api.listBankAccounts().then((r) => setAccs(r.rows)).catch(() => setAccs([])); }, []);
  const corpAccs = accs.filter((x) => x.is_active && x.currency === 'KRW' && x.corp_id === corpId);
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  // 목록은 입금 내역의 기간과 함께 오늘 뒤 예정도 보이게 — 끝 날짜가 오늘보다 앞이면 오늘 뒤 석 달까지.
  const listTo = to < today() ? '' : to;

  useEffect(() => {
    let cancelled = false;
    api.listBankInPlans({ corpId: defaultCorp, from, to: listTo || undefined })
      .then((r) => { if (!cancelled) setRows(r.rows); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [defaultCorp, from, listTo, nonce]);

  const reset = () => { setEditId(null); setAmount(''); setMemo(''); };
  const save = async () => {
    if (!corpId) { setError('법인을 고르세요.'); return; }
    if (!accountId) { setError('입금될 계좌를 고르세요.'); return; }
    setBusy(true); setError(null);
    try {
      await api.saveBankInPlan({ id: editId, corpId, accountId, date, amount, memo });
      reset(); setNonce((n) => n + 1);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const remove = async (r: BankInPlanRow) => {
    if (!window.confirm(`${r.plan_date} ${r.corp_name} ${money(r.amount)}원을 지울까요?`)) return;
    setBusy(true); setError(null);
    try { await api.deleteBankInPlan({ id: r.bank_in_plan_id }); if (editId === r.bank_in_plan_id) reset(); setNonce((n) => n + 1); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const edit = (r: BankInPlanRow) => {
    setEditId(r.bank_in_plan_id); setCorpId(r.corp_id); setAccountId(r.bank_account_id); setDate(r.plan_date); setAmount(r.amount.replace(/\.0+$/, '')); setMemo(r.memo ?? '');
  };
  const total = rows.filter((r) => r.counted).reduce((s, r) => s + Number(r.amount), 0);

  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">수기 입금(추정)</h2>
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      <p className="page-hint">
        넣은 금액은 유동성 시재의 입금에 더해집니다. 그 법인의 은행 거래내역이 올라온 날까지는 실제 입금이 있으니 더하지 않고, 그 뒤 날짜만 더합니다.
      </p>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" className="field w-40" value={date} max="9999-12-31" onChange={(e) => setDate(e.target.value)} />
          <select className="field w-36" value={corpId ?? ''} onChange={(e) => { setCorpId(e.target.value ? Number(e.target.value) : null); setAccountId(null); }}>
            {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.parent_corp_id ? `└ ${c.short_name}` : c.short_name}</option>)}
          </select>
          <select className="field w-52" value={accountId ?? ''} onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">입금될 계좌 고르기</option>
            {corpAccs.map((x) => <option key={x.bank_account_id} value={x.bank_account_id} title={`${x.account_no} · ${x.product ?? ''}`}>{planAccountLabel(x)}</option>)}
          </select>
          <MoneyInput className="field w-36 text-right" placeholder="금액" value={amount} onChange={setAmount} />
          <input className="field min-w-0 flex-1" placeholder="내용(예: ○○ 거래처 입금 예정)" value={memo} maxLength={300}
                 onChange={(e) => setMemo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !busy) void save(); }} />
          <button className="btn" disabled={busy || !amount} onClick={() => void save()}>{editId ? '고치기' : '넣기'}</button>
          {editId && <button className="btn-ghost" onClick={reset}>취소</button>}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <div className="max-h-[50vh] overflow-auto rounded border border-line">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface text-xs text-ink-soft">
            <tr><th className="th text-left">날짜</th><th className="th text-left">법인</th><th className="th text-left">계좌</th><th className="th text-right">금액</th><th className="th text-left">내용</th><th className="th text-left">유동성 시재</th><th className="th" /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td className="td text-ink-faint" colSpan={7}>넣은 수기 입금이 없습니다.</td></tr>}
            {rows.map((r) => (
              <tr key={r.bank_in_plan_id} className={editId === r.bank_in_plan_id ? 'bg-sky-50' : ''}>
                <td className="td whitespace-nowrap">{r.plan_date}</td>
                <td className="td whitespace-nowrap">{r.corp_name}</td>
                <td className="td whitespace-nowrap" title={r.account_no ?? ''}>{planAccountLabel(r) || <span className="text-ink-faint">—</span>}</td>
                <td className="td text-right tabular-nums">{money(r.amount)}</td>
                <td className="td">{r.memo ?? ''}</td>
                <td className="td whitespace-nowrap text-xs">
                  {r.counted ? <span className="text-brand-700">더함</span>
                    : <span className="text-ink-faint" title={`은행 거래내역이 ${r.last_tx_date} 까지 올라와 있어 실제 입금으로 계산합니다`}>안 더함(실제 입금 있음)</span>}
                </td>
                <td className="td whitespace-nowrap text-right">
                  {canWrite && <>
                    <button className="btn-ghost text-xs" onClick={() => edit(r)}>고치기</button>
                    <button className="btn-ghost text-xs text-red-600" disabled={busy} onClick={() => void remove(r)}>지우기</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && <div className="text-right text-sm">유동성 시재에 더하는 합계 <b>{money(total)}</b>원</div>}
    </div>
  );
}

/**
 * #472 입금 내역 표 위 「수기 입금(추정)」 줄 — 조회 기간·법인·계좌에 걸리는 수기 입금을 은행 거래와 나눠 보인다
 * (은행 거래 표에 섞으면 계산서 연결·구분·적요가 가짜 줄을 본다 — `_bank_in_plan.sql` 머리 주석). 없으면 안 그린다.
 */
export function BankInPlanStrip({ corpId, accountId, from, to, nonce, onOpen }: {
  corpId: number | null; accountId: number | null; from: string; to: string; nonce: number; onOpen: () => void;
}) {
  const [rows, setRows] = useState<BankInPlanRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.listBankInPlans({ corpId, accountId, from, to })
      .then((r) => { if (!cancelled) setRows(r.rows); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [corpId, accountId, from, to, nonce]);
  if (rows.length === 0) return null;
  const sum = rows.reduce((s, r) => s + Number(r.amount), 0);
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <b>수기 입금(추정) {rows.length}건 · {money(sum)}원</b>
        <span className="text-xs text-ink-soft">손으로 넣은 입금 예정 — 은행 거래내역이 아니라 아래 표·합계에는 들어가지 않습니다.</span>
        <button className="btn-ghost ml-auto text-xs" onClick={onOpen}>수기 입금 창 열기</button>
      </div>
      <table className="mt-1 w-full text-xs">
        <tbody>
          {rows.map((r) => (
            <tr key={r.bank_in_plan_id}>
              <td className="whitespace-nowrap py-0.5 pr-3">{r.plan_date}</td>
              <td className="whitespace-nowrap pr-3">{r.corp_name}</td>
              <td className="whitespace-nowrap pr-3" title={r.account_no ?? ''}>{planAccountLabel(r) || <span className="text-ink-faint">계좌 미지정</span>}</td>
              <td className="whitespace-nowrap pr-3 text-right tabular-nums">{money(r.amount)}</td>
              <td className="w-full">{r.memo ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
